import { CheckCircleOutline, HighlightOff, TuneOutlined } from '@mui/icons-material';
import { Button, CardContainer, Tag } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { EmptyState, LoadingState, ErrorState } from '@/components/common';
import { Navbar } from '@/components/super-admin/general';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import {
  useConfigChanges,
  useApproveConfigChange,
  useRejectConfigChange,
} from '@/hooks/queries/useOrganizationConfig';
import type {
  OrgConfigChange,
  OrgConfigChangeStatus,
  OrgConfigParamType,
} from '@/services/organizationConfig.service';

const PARAM_LABELS: Record<OrgConfigParamType, string> = {
  credit_settings: 'Créditos',
  transaction_limits: 'Límites de Transacción',
  payroll_config: 'Nómina',
};

const STATUS_LABELS: Record<OrgConfigChangeStatus, string> = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

const STATUS_VARIANTS: Record<OrgConfigChangeStatus, 'yellow' | 'green' | 'red'> = {
  pending: 'yellow',
  approved: 'green',
  rejected: 'red',
};

const TABS: { label: string; value: OrgConfigChangeStatus }[] = [
  { label: 'Pendientes', value: 'pending' },
  { label: 'Aprobados', value: 'approved' },
  { label: 'Rechazados', value: 'rejected' },
];

const formatDate = (d: string) =>
  new Date(d).toLocaleString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const ConfigReviewPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<OrgConfigChangeStatus>('pending');

  const { data: changes = [], isLoading, isError, refetch } = useConfigChanges({
    status: activeTab,
  });
  const approve = useApproveConfigChange();
  const reject = useRejectConfigChange();

  const handleApprove = async (change: OrgConfigChange) => {
    try {
      await approve.mutateAsync(change.id);
      toast.success('Cambio aprobado y aplicado.');
    } catch (_e) {
      toast.error('No se pudo aprobar el cambio.');
    }
  };

  const handleReject = async (change: OrgConfigChange) => {
    try {
      await reject.mutateAsync(change.id);
      toast.success('Cambio rechazado.');
    } catch (_e) {
      toast.error('No se pudo rechazar el cambio.');
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar navItems={superAdminNavItems} activeItem="Clientes" />

      <div className="px-4 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <TuneOutlined className="text-brand-blue" />
            <h1 className="text-2xl font-bold text-brand-white">
              Revisión de Parámetros Empresariales
            </h1>
          </div>
          <p className="text-muted mt-1">
            Aprueba o rechaza los cambios propuestos a los parámetros de cada organización. Los
            valores vigentes no cambian hasta que apruebas el cambio.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex gap-2">
          {TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.value
                  ? 'bg-brand-blue text-white'
                  : 'bg-white/5 text-muted hover:bg-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <LoadingState message="Cargando cambios..." />
        ) : isError ? (
          <ErrorState onRetry={() => void refetch()} />
        ) : changes.length === 0 ? (
          <EmptyState
            title="Sin cambios"
            description={`No hay cambios ${STATUS_LABELS[activeTab].toLowerCase()}s.`}
          />
        ) : (
          <div className="space-y-4">
            {changes.map(change => (
              <CardContainer key={change.id}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-brand-white">
                          {PARAM_LABELS[change.paramType]}
                        </h3>
                        <Tag variant={STATUS_VARIANTS[change.status]}>
                          {STATUS_LABELS[change.status]}
                        </Tag>
                      </div>
                      <p className="text-xs text-muted">
                        Organización: <span className="font-mono">{change.organizationId}</span>
                      </p>
                      <p className="text-xs text-muted">
                        Propuesto por <span className="font-mono">{change.proposedBy}</span> el{' '}
                        {formatDate(change.createdAt)}
                      </p>

                      {/* Proposed values */}
                      <div className="mt-3 p-3 bg-white/5 rounded-lg">
                        <p className="text-xs font-medium text-muted mb-1">Valores propuestos:</p>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                          {Object.entries(change.payload).map(([key, value]) => (
                            <p key={key} className="text-xs text-brand-white">
                              <span className="text-muted">{key}:</span> {String(value)}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>

                    {change.status === 'pending' && (
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button
                          variant="black"
                          size="small"
                          onClick={() => void handleApprove(change)}
                          disabled={approve.isPending || reject.isPending}
                        >
                          <CheckCircleOutline fontSize="small" className="mr-1" />
                          Aprobar
                        </Button>
                        <Button
                          variant="white"
                          size="small"
                          onClick={() => void handleReject(change)}
                          disabled={approve.isPending || reject.isPending}
                        >
                          <HighlightOff fontSize="small" className="mr-1" />
                          Rechazar
                        </Button>
                      </div>
                    )}
                  </div>

                  {change.status !== 'pending' && change.reviewedAt && (
                    <p className="text-xs text-muted mt-3">
                      Revisado por <span className="font-mono">{change.reviewedBy}</span> el{' '}
                      {formatDate(change.reviewedAt)}
                    </p>
                  )}
                </div>
              </CardContainer>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
